import { ArrowRight, Gift, MessageCircle } from "lucide-react";
import { useSession } from "@supabase/auth-helpers-react";
import { AdminNavigation } from "./admin/AdminNavigation";
import { useAdminView } from "./navigation/useAdminView";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { UserAvatar } from "./navigation/UserAvatar";

export function Navigation() {
  const session = useSession();
  const location = useLocation();
  const { isAdmin, isAdminView, loading } = useAdminView();
  const [currentRoute, setCurrentRoute] = useState("");

  useEffect(() => {
    const path = location.pathname;
    if (path === "/") setCurrentRoute("verse");
    else if (path.includes("/graceful-petitions") || path.includes("/testimonies") || 
             path.includes("/journal") || path.includes("/daily-devotionals")) {
      setCurrentRoute("community");
    }
    else if (path.includes("/give")) setCurrentRoute("give");
    else if (path.includes("/settings") || path.includes("/premium")) setCurrentRoute("settings");
  }, [location]);

  // If still loading, don't show any navigation
  if (loading) return null;
  
  // If user is admin and in admin view, show admin navigation
  if (isAdmin && isAdminView) {
    console.log("Rendering AdminNavigation component");
    return <AdminNavigation />;
  }

  const unavailableRoutes: string[] = [
    // Routes can be added here if needed
  ];

  const getNavigationClasses = () => {
    return "bg-app-surface-elevated/95 backdrop-blur-md border-t border-app-border shadow-lg";
  };

  console.log("Rendering standard Navigation component");
  return (
    <div className="fixed bottom-0 inset-x-0 z-50">
      <div className={`${getNavigationClasses()} w-full py-1 px-2 transition-colors duration-300`}>
        <div className="flex justify-around items-center max-w-xl mx-auto">
          <NavItem 
            icon={ArrowRight} 
            label="Verse" 
            isActive={currentRoute === "verse"}
            path="/"
          />
          
          <NavItem 
            icon={MessageCircle} 
            label="Community" 
            isActive={currentRoute === "community"}
            path="/graceful-petitions"
          />
          
          <NavItem 
            icon={Gift} 
            label="Give" 
            isActive={currentRoute === "give"}
            path="/give"
          />
          
          <NavItem 
            iconComponent={UserAvatar}
            label={session ? "Settings" : "Premium"}
            isActive={currentRoute === "settings"} 
            path={session ? "/settings" : "/premium"}
          />
        </div>
      </div>
    </div>
  );
}

interface NavItemProps {
  icon?: any;
  iconComponent?: any;
  label: string;
  isActive: boolean;
  path: string;
}

function NavItem({ icon: Icon, iconComponent: IconComponent, label, isActive, path }: NavItemProps) {
  
  const navigate = (path: string) => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
    window.location.href = path;
  };

  const classes = {
    container: isActive ? 'bg-app-accent/20' : '',
    icon: isActive ? 'text-app-accent' : 'text-app-text-secondary',
    label: isActive ? 'text-app-accent' : 'text-app-text-secondary'
  };
  
  return (
    <button
      className="flex flex-col items-center space-y-1 px-3 py-2 transition-colors duration-200"
      onClick={() => navigate(path)}
    >
      <div className={`p-1.5 rounded-full transition-colors ${classes.container}`}>
        {IconComponent ? (
          <IconComponent className={`w-5 h-5 transition-colors ${classes.icon}`} />
        ) : (
          <Icon className={`w-5 h-5 transition-colors ${classes.icon}`} />
        )}
      </div>
      <span className={`text-[10px] font-medium transition-colors ${classes.label}`}>
        {label}
      </span>
    </button>
  );
}
